import React from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { InputWithError } from "../components/common/InputWithError";
import { Loader2, Pencil, Trash2, X } from "lucide-react";

export function ManualMovementDialog({
  open, onClose, form, setForm, formErrors, validateField, onSubmit, saving, banche, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("dialogs.newMovement")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("dialogs.accountingDate")} *</Label>
              <DateInput value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className={formErrors.data ? "border-destructive" : ""} />
              {formErrors.data && <p className="text-xs text-destructive mt-1">{formErrors.data}</p>}
            </div>
            <div><Label>{t("dialogs.valueDate")}</Label><DateInput value={form.data_valuta} onChange={(e) => setForm({ ...form, data_valuta: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.type")}</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrata">{t("dialogs.income")}</SelectItem>
                  <SelectItem value="uscita">{t("dialogs.expense")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("dialogs.bank")}</Label>
              <SearchableSelect options={banche.map(b => ({ value: b.id, label: b.nome }))} value={form.banca_id} onValueChange={(v) => setForm({ ...form, banca_id: v })} placeholder={t("common.selectPlaceholder")} searchPlaceholder={t("common.searchPlaceholder")} testId="select-banca-manual" />
            </div>
          </div>
          <div>
            <Label>{t("common.description")} *</Label>
            <InputWithError value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} onValidate={() => validateField('descrizione', form.descrizione)} error={formErrors.descrizione} />
          </div>
          <div>
            <Label>{t('common.amount')} *</Label>
            <InputWithError type="number" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} onValidate={() => validateField('importo', form.importo)} error={formErrors.importo} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("dialogs.creating")}</> : t("dialogs.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AllocationDialog({
  open, onClose, allocDialog, allocType, setAllocType, allocForm, setAllocForm,
  editingAllocationId, cancelEditAllocation, startEditAllocation,
  onAddAllocation, onDeleteAllocation, calcolaResiduo,
  invoices, accounts, costCenters, allBankMovements, cardMovements, appartamenti,
  stipendiMesi = [],
  affittiAllocabili = [],
  pianiScadenzeRicorrenti = [],
  bolletteManualiAperte = [],
  onNeedGirocontoLists,
  formatCurrency, t
}) {
  // Formatta mese YYYY-MM -> "Gennaio 2026"
  const formatMese = (meseStr) => {
    if (!meseStr || meseStr.length < 7) return meseStr || "";
    const [y, m] = meseStr.split("-");
    const monthKeys = ["january","february","march","april","may","june","july","august","september","october","november","december"];
    const idx = parseInt(m, 10) - 1;
    return idx >= 0 && idx < 12 ? `${t(`months.${monthKeys[idx]}`)} ${y}` : meseStr;
  };
  
  // Trova info del mese stipendi selezionato
  const stipendiInfo = stipendiMesi.find(s => s.mese === allocForm.stipendi_mese_riferimento);
  
  // Trova info dell'affitto selezionato (mese)
  const affittoInfo = affittiAllocabili.find(a => a.mese === allocForm.affitto_mese_riferimento);
  
  // Trova piano e rata di scadenza ricorrente selezionati
  const pianoSelected = pianiScadenzeRicorrenti.find(p => p.piano_key === allocForm.piano_ricorrente_key);
  const rateAllocabili = pianoSelected ? (pianoSelected.rate || []).filter(r => !r.is_associata) : [];
  const rataSelected = pianoSelected ? (pianoSelected.rate || []).find(r => r.id === allocForm.scheduled_payment_id) : null;
  
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingAllocationId ? t("invoices.editAllocation") : t("dialogs.movementAmount")}</DialogTitle>
          <DialogDescription>
            {allocDialog.movement && `${allocDialog.movement.descrizione} - ${formatCurrency(allocDialog.movement.importo)}`}
          </DialogDescription>
        </DialogHeader>
        {allocDialog.movement && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="text-sm">
              <span className="text-muted-foreground">{t("dialogs.movementAmount")}:</span>
              <span className="font-medium ml-2">{formatCurrency(Math.abs(allocDialog.movement.importo))}</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">{t("dialogs.alreadyAllocated")}:</span>
              <span className="font-medium ml-2">{formatCurrency(allocDialog.allocations.filter(a => a.id !== editingAllocationId).reduce((sum, a) => sum + (a.importo || 0), 0))}</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">{t("dialogs.residue")}:</span>
              <span className={`font-bold ml-2 ${calcolaResiduo() > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {formatCurrency(calcolaResiduo())}
              </span>
            </div>
          </div>
        )}
        {allocDialog.allocations.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("dialogs.existingAllocations")}:</Label>
            <div className="border rounded-lg divide-y max-h-48 overflow-auto">
              {allocDialog.allocations.map((a) => (
                <div key={a.id} className={`flex items-center justify-between p-2 text-sm ${a.is_incoming_giroconto ? 'bg-blue-50/50 dark:bg-blue-900/10' : editingAllocationId === a.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                  <div className="flex-1">
                    {a.is_incoming_giroconto ? (
                      <>
                        <span className="font-medium">{formatCurrency(a.importo)}</span>
                        <span className="text-blue-600 ml-2">← Giroconto da: {a.source_banca_nome || 'Banca'} - {a.source_descrizione?.substring(0,40)}</span>
                        <span className="text-muted-foreground ml-1 text-xs">({new Date(a.source_data).toLocaleDateString('it-IT')})</span>
                      </>
                    ) : (
                      <>
                        <span className="font-medium">{formatCurrency(a.importo)}</span>
                        {a.invoice_numero && <span className="text-muted-foreground ml-2">Fatt. {a.invoice_numero}</span>}
                        {a.account_codice && <span className="text-muted-foreground ml-2">Conto {a.account_codice} - {a.account_nome}</span>}
                        {a.tipo_allocazione === "stipendi" && a.stipendi_mese_riferimento && (
                          <span className="text-purple-700 dark:text-purple-300 ml-2">Stipendi {formatMese(a.stipendi_mese_riferimento)}</span>
                        )}
                        {a.tipo_allocazione === "affitti" && a.affitto_mese_riferimento && (
                          <span className="text-fuchsia-700 dark:text-fuchsia-300 ml-2">Affitto {formatMese(a.affitto_mese_riferimento)}</span>
                        )}
                        {a.giroconto_bank_desc && <span className="text-blue-600 ml-2">→ Banca: {a.giroconto_bank_desc?.substring(0,30)}</span>}
                        {a.giroconto_card_desc && <span className="text-purple-600 ml-2">→ Carta: {a.giroconto_card_desc?.substring(0,30)}</span>}
                        {a.cost_center_codice && <Badge variant="secondary" className="ml-2">{a.cost_center_codice}</Badge>}
                      </>
                    )}
                  </div>
                  {!a.is_incoming_giroconto && (
                    <div className="flex gap-1">
                      {editingAllocationId === a.id ? (
                        <Button variant="ghost" size="sm" onClick={cancelEditAllocation}><X className="h-3 w-3" /></Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => startEditAllocation(a)} title={t("common.edit")}><Pencil className="h-3 w-3 text-blue-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => onDeleteAllocation(a.id)} title={t("common.delete")}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("dialogs.allocationType")}</Label>
              <Select value={allocType} onValueChange={(v) => { 
                setAllocType(v); 
                setAllocForm({...allocForm, giroconto_bank_movement_id: "", giroconto_card_movement_id: "", bolletta_manuale_id: ""}); 
                if (v === "giroconto" && typeof onNeedGirocontoLists === 'function') onNeedGirocontoLists();
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fattura">{t("dialogs.invoice")}</SelectItem>
                  <SelectItem value="conto">{t("dialogs.account")}</SelectItem>
                  <SelectItem value="assicurazione">{t("dialogs.vehicleInsurance")}</SelectItem>
                  <SelectItem value="bollo_auto">{t("dialogs.vehicleTax")}</SelectItem>
                  <SelectItem value="carburante">{t("dialogs.fuel")}</SelectItem>
                  <SelectItem value="bolletta">{t("dialogs.bill")}</SelectItem>
                  <SelectItem value="manutenzione_appartamento">{t("dialogs.apartmentMaintenance")}</SelectItem>
                  <SelectItem value="stipendi">{t("dialogs.salariesPersonnel")}</SelectItem>
                  <SelectItem value="affitti">{t("dialogs.rentApartments")}</SelectItem>
                  <SelectItem value="scadenze_ricorrenti">{t("dialogs.recurringDeadlines")}</SelectItem>
                  <SelectItem value="giroconto">{t("dialogs.transfer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common.amount")}</Label>
              <Input 
                type="number" step="0.01" 
                value={allocForm.importo} 
                onChange={(e) => setAllocForm({ ...allocForm, importo: e.target.value })} 
                disabled={allocType === "bolletta" || allocType === "manutenzione_appartamento"}
              />
            </div>
          </div>
          {allocType === "fattura" && (
            <div className="space-y-2">
              <div>
                <Label>{t("dialogs.invoice")}</Label>
                <SearchableSelect
                  options={invoices.map(i => ({ value: i.id, label: `${i.numero} - ${i.fornitore_cliente} (${formatCurrency(i.totale)})${i.tipo === 'attiva' ? ' [Cliente]' : i.tipo === 'passiva' ? ' [Fornitore]' : ''}` }))}
                  value={allocForm.invoice_id} onValueChange={(v) => setAllocForm({ ...allocForm, invoice_id: v })}
                  placeholder={t("dialogs.selectInvoice")}
                />
              </div>
              <div className="flex items-center gap-2 p-2 bg-blue-50/50 dark:bg-blue-950/20 rounded-md border border-blue-200/50">
                <input
                  type="checkbox"
                  id="compensazione-check"
                  checked={(parseFloat(allocForm.importo) || 0) < 0}
                  onChange={(e) => {
                    const val = parseFloat(allocForm.importo) || 0;
                    if (e.target.checked && val > 0) {
                      setAllocForm({ ...allocForm, importo: String(-val) });
                    } else if (!e.target.checked && val < 0) {
                      setAllocForm({ ...allocForm, importo: String(Math.abs(val)) });
                    }
                  }}
                  className="h-4 w-4 cursor-pointer"
                  data-testid="checkbox-compensazione"
                />
                <Label htmlFor="compensazione-check" className="text-xs cursor-pointer">
                  <span className="font-semibold text-blue-700 dark:text-blue-300">{t("dialogs.compensationLabel")}</span> — {t("dialogs.compensationHelp")}
                </Label>
              </div>
            </div>
          )}
          {(allocType === "conto" || allocType === "assicurazione" || allocType === "bollo_auto" || allocType === "carburante") && (
            <div>
              <Label>{t('movements.account')}</Label>
              <SearchableSelect
                options={accounts.map(a => ({ value: a.id, label: `${a.codice} - ${a.nome || a.descrizione || ''}` }))}
                value={allocForm.account_id} onValueChange={(v) => setAllocForm({ ...allocForm, account_id: v })}
                placeholder={t("common.selectPlaceholder")}
              />
              {(allocType === "assicurazione" || allocType === "bollo_auto" || allocType === "carburante") && (
                <p className="text-xs text-muted-foreground mt-1">
                  Il CDC sarà determinato dal collegamento al {allocType === "assicurazione" ? "record assicurazione" : allocType === "bollo_auto" ? "bollo auto" : "rifornimento"} nella tab dedicata.
                </p>
              )}
            </div>
          )}
          {(allocType === "bolletta" || allocType === "manutenzione_appartamento") && (
            <div className="space-y-3">
              {allocType === "bolletta" && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="bolletta-manuale-picker">
                  <p className="font-medium text-sm text-amber-900">Bolletta manuale (opzionale)</p>
                  <p className="text-xs text-amber-800">
                    Se questo movimento chiude una bolletta creata manualmente (non derivante da fattura), selezionala qui. Verrà marcata come pagata.
                    Se lasci vuoto, l&apos;allocazione dovrà essere completata dalla tab &quot;Bollette&quot;.
                  </p>
                  <SearchableSelect
                    options={[
                      { value: "", label: "— Nessuna bolletta manuale —" },
                      ...bolletteManualiAperte.map(b => ({
                        value: b.id,
                        label: `${b.fornitore || 'N/D'} - ${b.descrizione || ''} - ${formatCurrency(b.residuo)} (${b.data || ''})`
                      }))
                    ]}
                    value={allocForm.bolletta_manuale_id || ""}
                    onValueChange={(v) => {
                      const selected = bolletteManualiAperte.find(b => b.id === v);
                      setAllocForm({
                        ...allocForm,
                        bolletta_manuale_id: v || "",
                        // Pre-popola l'importo con il residuo della bolletta e conto/cdc se disponibili
                        ...(selected ? {
                          importo: String(selected.residuo || 0),
                          account_id: selected.conto_id || allocForm.account_id,
                          cost_center_id: selected.centro_costo_id || allocForm.cost_center_id,
                        } : {})
                      });
                    }}
                    placeholder="Seleziona bolletta manuale..."
                    searchPlaceholder="Cerca per fornitore/descrizione..."
                    testId="select-bolletta-manuale"
                  />
                </div>
              )}
              {!allocForm.bolletta_manuale_id && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                  <p className="font-medium mb-1">{allocType === "bolletta" ? t("dialogs.bill") : t("dialogs.apartmentMaintenance")}</p>
                  <p>{t("dialogs.billNote").replace("{tipo}", allocType === "bolletta" ? t("dialogs.bill").toLowerCase() : t("dialogs.apartmentMaintenance").toLowerCase())} <strong>{t("dialogs.billTab")}</strong>.</p>
                </div>
              )}
            </div>
          )}
          {allocType === "stipendi" && (
            <div className="space-y-3">
              <div>
                <Label>{t("allocations.salaryMonth")} *</Label>
                <SearchableSelect
                  options={stipendiMesi.map(s => ({
                    value: s.mese,
                    label: `${formatMese(s.mese)} - ${t("common.total")} ${formatCurrency(s.totale)} - ${t("dialogs.residue")} ${formatCurrency(s.residuo)}`
                  }))}
                  value={allocForm.stipendi_mese_riferimento}
                  onValueChange={(v) => {
                    const selected = stipendiMesi.find(s => s.mese === v);
                    const residuoMov = calcolaResiduo();
                    const suggestedImporto = selected
                      ? Math.min(Math.max(0, selected.residuo), residuoMov)
                      : residuoMov;
                    setAllocForm({
                      ...allocForm,
                      stipendi_mese_riferimento: v,
                      importo: suggestedImporto > 0 ? String(suggestedImporto.toFixed(2)) : allocForm.importo
                    });
                  }}
                  placeholder={stipendiMesi.length === 0 ? t("allocations.noPersonnelMonths") : t("dialogs.selectSalaryMonth")}
                  testId="alloc-stipendi-mese"
                />
              </div>
              {stipendiInfo && (
                <div className="grid grid-cols-3 gap-2 p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 rounded-lg text-sm" data-testid="stipendi-mese-summary">
                  <div>
                    <p className="text-xs text-purple-700 dark:text-purple-300 uppercase tracking-wider font-semibold">{t("dialogs.monthlyTotal")}</p>
                    <p className="font-mono font-bold">{formatCurrency(stipendiInfo.totale)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700 dark:text-purple-300 uppercase tracking-wider font-semibold">{t("dialogs.alreadyAllocated")}</p>
                    <p className="font-mono font-bold">{formatCurrency(stipendiInfo.allocato)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700 dark:text-purple-300 uppercase tracking-wider font-semibold">{t("dialogs.residue")}</p>
                    <p className={`font-mono font-bold ${stipendiInfo.residuo > 0 ? 'text-emerald-600' : stipendiInfo.residuo < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {formatCurrency(stipendiInfo.residuo)}
                    </p>
                  </div>
                  {parseFloat(allocForm.importo || 0) > stipendiInfo.residuo && stipendiInfo.residuo >= 0 && (
                    <div className="col-span-3 text-xs text-amber-700 dark:text-amber-400 mt-1">
                      {t("dialogs.amountSurpassesResidue")}
                    </div>
                  )}
                </div>
              )}
              <div className="p-3 bg-purple-50/50 border border-purple-200/50 rounded-lg text-xs text-purple-800 dark:text-purple-300">
                {t("dialogs.salaryAlreadyBalance")} <strong>{t("dialogs.salaryThroughExtra")}</strong>{t("dialogs.salaryTracksOnly")}
              </div>
            </div>
          )}
          {allocType === "affitti" && (
            <div className="space-y-3">
              <div>
                <Label>{t("allocations.rentMonth")} *</Label>
                <SearchableSelect
                  options={affittiAllocabili.map(a => ({
                    value: a.mese,
                    label: `${formatMese(a.mese)} - ${a.appartamenti_count} app - Tot ${formatCurrency(a.canone_totale)} - ${t("dialogs.residue")} ${formatCurrency(a.residuo)}`
                  }))}
                  value={allocForm.affitto_mese_riferimento}
                  onValueChange={(v) => {
                    const selected = affittiAllocabili.find(a => a.mese === v);
                    const residuoMov = calcolaResiduo();
                    const suggestedImporto = selected
                      ? Math.min(Math.max(0, selected.residuo), residuoMov)
                      : residuoMov;
                    setAllocForm({
                      ...allocForm,
                      affitto_mese_riferimento: v,
                      affitto_appartamento_id: "",
                      importo: suggestedImporto > 0 ? String(suggestedImporto.toFixed(2)) : allocForm.importo
                    });
                  }}
                  placeholder={affittiAllocabili.length === 0 ? t("allocations.noRentMonths") : t("dialogs.selectMonth")}
                  testId="alloc-affitto-mese"
                />
              </div>
              {affittoInfo && (
                <div className="grid grid-cols-3 gap-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-950/20 border border-fuchsia-200 dark:border-fuchsia-900/40 rounded-lg text-sm" data-testid="affitto-summary">
                  <div>
                    <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300 uppercase tracking-wider font-semibold">{t("dialogs.monthlyRents")} ({affittoInfo.appartamenti_count} app)</p>
                    <p className="font-mono font-bold">{formatCurrency(affittoInfo.canone_totale)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300 uppercase tracking-wider font-semibold">{t("dialogs.alreadyPaid")}</p>
                    <p className="font-mono font-bold">{formatCurrency(affittoInfo.allocato)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300 uppercase tracking-wider font-semibold">{t("dialogs.residue")}</p>
                    <p className={`font-mono font-bold ${affittoInfo.residuo > 0 ? 'text-emerald-600' : affittoInfo.residuo < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {formatCurrency(affittoInfo.residuo)}
                    </p>
                  </div>
                  {parseFloat(allocForm.importo || 0) > affittoInfo.residuo && affittoInfo.residuo >= 0 && (
                    <div className="col-span-3 text-xs text-amber-700 dark:text-amber-400 mt-1">
                      {t("dialogs.rentAmountSurpasses")}
                    </div>
                  )}
                </div>
              )}
              <div className="p-3 bg-fuchsia-50/50 border border-fuchsia-200/50 rounded-lg text-xs text-fuchsia-800 dark:text-fuchsia-300">
                {t("dialogs.rentTracksOnly")}
              </div>
            </div>
          )}
          {allocType === "scadenze_ricorrenti" && (
            <div className="space-y-3">
              <div>
                <Label>{t("dialogs.recurringPlan")} *</Label>
                <SearchableSelect
                  options={pianiScadenzeRicorrenti.map(p => {
                    const restanti = (p.rate || []).filter(r => !r.is_associata).length;
                    return {
                      value: p.piano_key,
                      label: `${p.descrizione || '—'}${p.beneficiario ? ` · ${p.beneficiario}` : ''} (${restanti}/${p.totale_rate || (p.rate || []).length} rate restanti)`
                    };
                  })}
                  value={allocForm.piano_ricorrente_key}
                  onValueChange={(v) => setAllocForm({
                    ...allocForm,
                    piano_ricorrente_key: v,
                    scheduled_payment_id: ""
                  })}
                  placeholder={pianiScadenzeRicorrenti.length === 0 ? t("dialogs.noRecurringPlans") : t("dialogs.selectRecurringPlan")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  testId="alloc-piano-ricorrente"
                />
              </div>
              {pianoSelected && (
                <div>
                  <Label>{t("dialogs.installment")} *</Label>
                  <SearchableSelect
                    options={rateAllocabili.map(r => ({
                      value: r.id,
                      label: `${t("dialogs.installment")} ${r.numero_rata || '—'}/${pianoSelected.totale_rate || ''} · ${r.data_scadenza || ''} · ${formatCurrency(r.importo)}`
                    }))}
                    value={allocForm.scheduled_payment_id}
                    onValueChange={(v) => {
                      const r = (pianoSelected.rate || []).find(x => x.id === v);
                      const residuoMov = calcolaResiduo();
                      const suggested = r ? Math.min(Math.abs(r.importo || 0), residuoMov) : residuoMov;
                      setAllocForm({
                        ...allocForm,
                        scheduled_payment_id: v,
                        importo: suggested > 0 ? String(suggested.toFixed(2)) : allocForm.importo
                      });
                    }}
                    placeholder={rateAllocabili.length === 0 ? t("dialogs.noInstallmentsLeft") : t("dialogs.selectInstallment")}
                    testId="alloc-rata-ricorrente"
                  />
                </div>
              )}
              {rataSelected && (
                <div className="p-3 bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900/40 rounded-lg text-sm" data-testid="rata-summary">
                  <p className="font-semibold text-sky-800 dark:text-sky-300">{pianoSelected.descrizione} · {t("dialogs.installment")} {rataSelected.numero_rata}/{pianoSelected.totale_rate || '—'}</p>
                  <p className="text-xs text-sky-700 dark:text-sky-400 mt-1">{t("invoices.dueDate")}: {rataSelected.data_scadenza || '—'} · {t("common.amount")}: <strong>{formatCurrency(rataSelected.importo)}</strong></p>
                  <p className="text-xs text-sky-600 dark:text-sky-500 mt-1">{t("dialogs.recurringMarkPaidHint")}</p>
                </div>
              )}
            </div>
          )}
          {allocType === "giroconto" && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                {t("dialogs.transferLinkInfo")}
              </div>
              <div>
                <Label>{t("dialogs.movementBank")}</Label>
                <SearchableSelect
                  options={allBankMovements.filter(m => m.id !== allocDialog.movement?.id && m.banca_id !== allocDialog.movement?.banca_id).map(m => ({ value: m.id, label: `${m.data} - ${m.banca_nome || 'N/A'} - ${m.descrizione?.substring(0, 40)} (${formatCurrency(m.importo)})` }))}
                  value={allocForm.giroconto_bank_movement_id} onValueChange={(v) => setAllocForm({ ...allocForm, giroconto_bank_movement_id: v, giroconto_card_movement_id: "" })}
                  placeholder={t("dialogs.selectBankMovement")}
                />
              </div>
              <div className="text-center text-sm text-muted-foreground">{t("dialogs.or")}</div>
              <div>
                <Label>{t("dialogs.cardMovement")}</Label>
                <SearchableSelect
                  options={cardMovements.map(m => {
                    const last4 = m.carta_numero ? ` (•••• ${String(m.carta_numero).slice(-4)})` : '';
                    const intest = m.intestatario || '';
                    return { value: m.id, label: `${m.data} - ${intest}${last4} - ${m.descrizione?.substring(0, 40)} (${formatCurrency(m.importo)})` };
                  })}
                  value={allocForm.giroconto_card_movement_id} onValueChange={(v) => setAllocForm({ ...allocForm, giroconto_card_movement_id: v, giroconto_bank_movement_id: "" })}
                  placeholder={t("dialogs.selectCardMovement")}
                />
              </div>
            </div>
          )}
          {allocType === "conto" && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="col-span-2 text-xs text-blue-700 -mb-1">
                {t("dialogs.competenceDatesInfo")}
              </div>
              <div>
                <Label className="text-xs">{t("dialogs.competenceStart")}</Label>
                <DateInput
                  value={allocForm.data_inizio_competenza}
                  onChange={(e) => setAllocForm({ ...allocForm, data_inizio_competenza: e.target.value })}
                  data-testid="alloc-data-inizio-competenza"
                />
              </div>
              <div>
                <Label className="text-xs">{t("dialogs.competenceEnd")}</Label>
                <DateInput
                  value={allocForm.data_fine_competenza}
                  onChange={(e) => setAllocForm({ ...allocForm, data_fine_competenza: e.target.value })}
                  data-testid="alloc-data-fine-competenza"
                />
              </div>
            </div>
          )}
          <div>
            <Label>{t("costCenters.title")}</Label>
            <SearchableSelect
              options={costCenters.map(c => ({ value: c.id, label: `${c.codice} - ${c.nome}` }))}
              value={allocForm.cost_center_id} onValueChange={(v) => setAllocForm({ ...allocForm, cost_center_id: v })}
              placeholder={t("common.selectPlaceholder")}
            />
          </div>
          <div>
            <Label>{t("common.notes")}</Label>
            <Input value={allocForm.note} onChange={(e) => setAllocForm({ ...allocForm, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
          {editingAllocationId && <Button variant="outline" onClick={cancelEditAllocation}>{t("dialogs.cancelEdit")}</Button>}
          <Button onClick={onAddAllocation}>{editingAllocationId ? t("common.save") : t("invoices.addAllocation")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteMovementDialogs({
  deleteSingleDialog, setDeleteSingleDialog, onDeleteSingle, deletingMovement,
  deleteMultipleDialog, setDeleteMultipleDialog, onBulkDelete, selectedCount, deletingBulk,
  deleteAllDialog, setDeleteAllDialog, onDeleteAll, totalCount, t
}) {
  return (
    <>
      <Dialog open={!!deleteSingleDialog} onOpenChange={(v) => !v && setDeleteSingleDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("dialogs.deleteBankMovementConfirm")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSingleDialog(null)} disabled={!!deletingMovement}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => onDeleteSingle(deleteSingleDialog)} disabled={!!deletingMovement} data-testid="confirm-delete-single-bank">
              {deletingMovement ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("dialogs.deleting")}</> : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteMultipleDialog} onOpenChange={setDeleteMultipleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("dialogs.deleteBulkMovementsPrefix")} <strong>{selectedCount}</strong> {selectedCount === 1 ? t("dialogs.bankMovement") : t("dialogs.bankMovements")}. {t("dialogs.actionIrreversible")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMultipleDialog(false)} disabled={deletingBulk}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={onBulkDelete} disabled={deletingBulk} data-testid="confirm-bulk-delete-bank">
              {deletingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("dialogs.deleting")}</> : `${t("dialogs.deleteCount")} ${selectedCount}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteAllDialog} onOpenChange={setDeleteAllDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("confirm.confirmTotalDeletion")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("dialogs.deleteAllPrefix")} <strong>{t("dialogs.deleteAllItems")} {totalCount}</strong> {t("dialogs.deleteAllSuffix")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialog(false)} disabled={deletingBulk}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={onDeleteAll} disabled={deletingBulk} data-testid="confirm-delete-all-bank">
              {deletingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("dialogs.deleting")}</> : `${t("dialogs.deleteAll")} (${totalCount})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
